import { useState } from "react";
import { Server, RefreshCw, Unplug, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { FolderFilter } from "@/components/sources/PlexFolderFilter";
import { PlexChooseFolder } from "@/components/sources/PlexChooseFolder";
import { PillButton, SourceShell, StatusPill, SyncLine, useSourceStatus } from "@/components/sources/shared";

const PresetConnect = ({ preset, onDone }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/plex/connect-preset"); onDone(data); toast.success(`Connected to ${preset.name} — importing`); }
    catch (err) { toast.error(formatApiError(err)); onDone(null); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="plex-preset">
      <PillButton onClick={go} disabled={busy} testid="plex-preset-connect-button">{busy ? <Loader2 size={14} className="animate-spin" /> : <Server size={14} />}Connect {preset.name}</PillButton>
      <span className="text-xs text-zinc-500">{busy ? "Finding the fastest route to your server…" : "Imports the RAWMKV library by default — you can choose another folder after connecting"}</span>
    </div>
  );
};

const ConnectArea = ({ preset, onDone }) => (preset ? <PresetConnect preset={preset} onDone={onDone} /> : (
  <p className="text-sm text-zinc-500" data-testid="plex-admin-only">An administrator connects the Plex server for this site.</p>
));

const Sections = ({ st, onSave, busy }) => {
  const [keys, setKeys] = useState(st.section_keys || st.sections.map((s) => s.key));
  const toggle = (k) => setKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  return (
    <div className="mt-2" data-testid="plex-sections">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Libraries to import</p>
      <div className="flex flex-wrap gap-3">
        {st.sections.map((s) => (
          <label key={s.key} className="flex cursor-pointer items-center gap-2 rounded-full border border-zinc-200 px-4 py-2 text-sm" data-testid={`plex-section-${s.key}`}>
            <Checkbox checked={keys.includes(s.key)} onCheckedChange={() => toggle(s.key)} />{s.title}<span className="font-mono text-[10px] text-zinc-400">{s.type === "show" ? "TV" : "Movies"}</span>
          </label>
        ))}
      </div>
      <PillButton variant="light" onClick={() => onSave(keys)} disabled={busy || !keys.length} testid="plex-save-sections">Save & import</PillButton>
    </div>
  );
};

export const PlexCard = () => {
  const [st, setSt, refresh] = useSourceStatus("/plex");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.connected !== undefined) setSt(r.data); else await refresh(); if (ok) toast.success(ok); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <SourceShell icon={Server} title="Plex Media Server" testid="plex-card" badge={<StatusPill status={st} testid="plex-status" />}
      subtitle={st?.connected ? `${st.server_name} · ${st.server_host} · v${st.version || "?"}` : "Plex converts MKV, AVI and other formats for the browser."}>
      {!st ? <Loader2 className="animate-spin text-zinc-300" /> : !st.connected ? (
        <ConnectArea preset={st.preset} onDone={(d) => (d ? setSt(d) : refresh())} />
      ) : (
        <div className="space-y-5">
          {st.sections?.length > 0 && <PlexChooseFolder st={st} busy={busy || st.sync_status === "running"}
            onChoose={(body) => run(() => api.put("/plex/choose", body), `Importing ${body.folder ? `“${body.folder}”` : "library"}`)} />}
          {st.sections?.length > 0 && (
            <details className="rounded-lg border border-zinc-200 p-4" data-testid="plex-advanced">
              <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400" data-testid="plex-advanced-toggle">Advanced · several libraries or folders</summary>
              <div className="mt-4 space-y-5">
              <Sections key={(st.section_keys || []).join()} st={st} busy={busy} onSave={(keys) => run(() => api.put("/plex/sections", { keys }), "Importing selected libraries")} />
            <div className="space-y-2" data-testid="plex-folder-filters">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Limit to folders (optional)</p>
              {st.sections.filter((s) => !st.section_keys || st.section_keys.includes(s.key)).map((s) => (
                <FolderFilter key={s.key} section={s} folders={st.folder_filters?.[s.key]} busy={busy || st.sync_status === "running"}
                  onChange={(section, folders) => run(() => api.put("/plex/folders", { section, folders }), folders.length ? "Re-importing from selected folder" : "Re-importing whole library")} />
              ))}
            </div>
              </div>
            </details>
          )}
          <div className="flex flex-wrap gap-2">
            <PillButton onClick={() => run(() => api.post("/plex/sync"), "Import started")} disabled={busy || st.sync_status === "running"} testid="plex-sync-button"><RefreshCw size={14} className={st.sync_status === "running" ? "animate-spin" : ""} />Sync now</PillButton>
            <PillButton variant="danger" onClick={() => window.confirm("Disconnect Plex and remove its videos from Cucoloris Action Harbor? Nothing changes on your server.") && run(() => api.delete("/plex"), "Plex disconnected")} disabled={busy} testid="plex-disconnect-button"><Unplug size={14} />Disconnect</PillButton>
          </div>
        </div>
      )}
      {st?.via_relay && <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="plex-relay-note">Connected through Plex Relay because your server's Remote Access port isn't reachable. Relay speed is limited (about 1–2 Mbps), so converted video plays at lower quality. To fix this, forward the Remote Access port on your router.</p>}
      {st?.connected && <p className="mt-4 text-xs text-zinc-500" data-testid="plex-watch-sync-note">Watched status and resume points sync both ways with your Plex apps.</p>}
      <SyncLine status={st} testid="plex-sync-status" />
    </SourceShell>
  );
};
